import {
  Accordion,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Grid,
  Group,
  Loader,
  MultiSelect,
  Progress,
  SegmentedControl,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { formatCount, formatCurrency, formatRatio } from '../../helpers/atAGlance';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import MetricsSelector from '../MetricsSelector';

// Self-contained super-admin surface for the feed bandit: an on/off kill switch
// for the domain, a Configuration panel (which placements take part, and which
// feeds are their arms) staged behind a Save, and a Results panel grouped page
// (or Emails, or Search) -> placement -> arms. The platform-wide feed-type
// allowlist lives in FeedTypeAllowlistPanel — it is not domain-scoped. Kept
// dependency-free of the settings page so it can be lifted into the data
// platform later — it reads its own state from the API and takes only the
// domain (for currency) as a prop.
const feedSurfaceLabel = { web: 'On-site', channel: 'Email' };

// The API's ineligibleReason enum, as something an operator can act on: this is
// the answer to "why can't I select this feed?".
const ineligibleReasonLabel = {
  'personalized-algorithm': 'personalised — ranks differently per shopper',
  'context-dependent': 'context-dependent — needs a product or cart to rank',
  'not-allowlisted': 'this feed type is not allowlisted',
  'ads-scope': 'ads-only feed',
  'per-feed-context-action': 'has a per-feed context action',
};

// Participation defaults to on, so a placement the API has not marked is in the
// test — that is the behaviour every domain had before per-placement opt-out.
const participates = (placement) => placement?.banditEnabled !== false;

const emptyConfig = {
  feedIds: [],
  recommenderTokens: [],
  emailIds: [],
  search: false,
  recommenderFeeds: {},
  emailBlockFeeds: {},
};

const webBlocks = (state) => (state?.placements?.web || []).flatMap((page) => page.blocks || []);

// Whether the API that answered describes per-placement feed overrides at all.
// It may deploy after this panel, and an older payload carries no `feedOverride`
// on a web block and no `blocks` on an email — in which case the UI offers no
// per-block control and the PUT omits that surface's override map, rather than
// sending one that would read as "every placement reverts to inherit".
const webOverridesDescribed = (state) =>
  Array.isArray(state?.placements?.web) && webBlocks(state).every((block) => 'feedOverride' in block);

const emailOverridesDescribed = (state) =>
  Array.isArray(state?.placements?.email) && state.placements.email.every((email) => Array.isArray(email.blocks));

// An override is a subset of the curated pool, or null for "inherit the whole
// pool" — which is NOT the same as listing every pooled feed, because inherit
// keeps picking up feeds added to the pool later.
const overrideFromState = (feedOverride) => (Array.isArray(feedOverride) ? [...feedOverride] : null);

// The saveable configuration, read out of the GET payload. Every nested shape is
// defaulted individually: useFetch resolves an empty object on a 202/204 or an
// unparseable body, so the payload can be truthy while any key is missing.
const configFromState = (state) => ({
  feedIds: [...(state?.pool?.web || []), ...(state?.pool?.channel || [])].filter((f) => f.inPool).map((f) => f.id),
  recommenderTokens: webBlocks(state)
    .filter(participates)
    .map((block) => block.token),
  emailIds: (state?.placements?.email || []).filter(participates).map((email) => email.emailId),
  // A domain with no search placement at all has nothing to opt in or out of —
  // send false rather than defaulting to true, so the payload only asserts
  // participation for a placement the UI actually offered a checkbox for.
  search: state?.placements?.search ? participates(state.placements.search) : false,
  recommenderFeeds: Object.fromEntries(
    webBlocks(state).map((block) => [block.token, overrideFromState(block.feedOverride)])
  ),
  emailBlockFeeds: Object.fromEntries(
    (state?.placements?.email || []).map((email) => [
      email.emailId,
      Object.fromEntries(
        (email.blocks || []).map((block) => [block.blockIndex, overrideFromState(block.feedOverride)])
      ),
    ])
  ),
});

// An override may only name feeds that are in the curated pool as the same
// request leaves it, so a feed the operator unticked from the draft pool is
// dropped here rather than sent and rejected with a 400. "In the pool" is the
// API's reading of it: the pooled feeds that are still eligible. A feed can be
// `{ eligible: false, inPool: true }` (pooled before the allowlist narrowed),
// but the API never serves it as an arm, drops it from the pool on the same
// save, and rejects an override naming it — so it is pruned here too.
const prunedOverrides = (overrides, feedIds) =>
  Object.fromEntries(
    Object.entries(overrides).map(([key, ids]) => [
      key,
      Array.isArray(ids) ? ids.filter((id) => feedIds.includes(id)) : null,
    ])
  );

// The `placements` half of the config PUT. Each surface carries FULL membership,
// so an empty array means "take every block on this surface out of the test" —
// which is exactly what the draft holds for a surface the payload never
// described. PUT /config is partial-safe by design (an absent surface is left
// alone), so a surface the API did not report is OMITTED rather than sent
// empty: unticking every block is a deliberate act, and must not be
// indistinguishable from a payload we could not read.
const placementsPayload = (state, draft) => {
  const payload = {};
  // Pruning is against `draft.feedIds` — the exact pool this request's
  // `feedIds` sends — and `configBody` only sends that pool when both halves
  // were readable. Otherwise the draft's feedIds is a partial list, and
  // pruning against it would turn an override naming an unreadable feed into
  // [] — "primary only" — which the API would store.
  const eligibleIds = new Set(
    [...(state?.pool?.web || []), ...(state?.pool?.channel || [])]
      .filter((feed) => feed.eligible)
      .map((feed) => feed.id)
  );
  const prune = (overrides) =>
    Array.isArray(state?.pool?.web) && Array.isArray(state?.pool?.channel)
      ? prunedOverrides(
          overrides,
          draft.feedIds.filter((id) => eligibleIds.has(id))
        )
      : overrides;
  if (Array.isArray(state?.placements?.web)) payload.recommenderTokens = draft.recommenderTokens;
  if (Array.isArray(state?.placements?.email)) payload.emailIds = draft.emailIds;
  if (state?.placements?.search) payload.search = draft.search;
  // A block's primary always runs as an arm and is never listed in its override,
  // even when a stored override already names it (the block was repointed at it).
  const withoutPrimary = (overrides, primaryOf) =>
    Object.fromEntries(
      Object.entries(overrides).map(([key, ids]) => {
        const primaryId = primaryOf(key);
        return [key, Array.isArray(ids) && primaryId != null ? ids.filter((id) => id !== primaryId) : ids];
      })
    );
  if (webOverridesDescribed(state)) {
    const primaryByToken = Object.fromEntries(webBlocks(state).map((block) => [block.token, block.primaryFeed?.id]));
    payload.recommenderFeeds = prune(withoutPrimary(draft.recommenderFeeds, (token) => primaryByToken[token]));
  }
  if (emailOverridesDescribed(state)) {
    payload.emailBlockFeeds = Object.fromEntries(
      Object.entries(draft.emailBlockFeeds).map(([emailId, blocks]) => {
        const email = state.placements.email.find((e) => String(e.emailId) === emailId);
        const primaryOf = (index) => (email?.blocks || []).find((b) => String(b.blockIndex) === index)?.primaryFeed?.id;
        return [emailId, prune(withoutPrimary(blocks, primaryOf))];
      })
    );
  }
  return payload;
};

// The config PUT body. `feedIds` is guarded like the placement surfaces: an
// unreadable pool makes `configFromState` yield [], which this endpoint reads as
// a deliberate clear — it empties the curated pool and stamps the one-way marker
// that disables the first-activation autofill for good. An absent `feedIds`
// leaves the pool alone. BOTH surfaces must be present, since one alone yields a
// `feedIds` missing the other's feeds, and a partial read must omit rather than
// answer partially. An empty-but-present pool is a readable answer, and sends [].
const configBody = (state, draft) => {
  const payload = { placements: placementsPayload(state, draft) };
  if (Array.isArray(state?.pool?.web) && Array.isArray(state?.pool?.channel)) payload.feedIds = draft.feedIds;
  return payload;
};

// Order-independent identity of a map of overrides: its keys sorted, its values
// sorted, and anything that is not an array flattened to the one null that
// means inherit.
const overridesKey = (overrides) =>
  Object.fromEntries(
    Object.keys(overrides || {})
      .sort()
      .map((key) => [key, Array.isArray(overrides[key]) ? [...overrides[key]].sort() : null])
  );

// Order-independent identity of a configuration, for the dirty check.
const configKey = (config) =>
  JSON.stringify({
    feedIds: [...config.feedIds].sort(),
    recommenderTokens: [...config.recommenderTokens].sort(),
    emailIds: [...config.emailIds].sort(),
    search: config.search,
    recommenderFeeds: overridesKey(config.recommenderFeeds),
    emailBlockFeeds: Object.fromEntries(
      Object.keys(config.emailBlockFeeds || {})
        .sort()
        .map((emailId) => [emailId, overridesKey(config.emailBlockFeeds[emailId])])
    ),
  });

// How many of a group's members are in the test, out of how many there are.
// Everything participates by default, so "all in" is the unremarkable case and
// gets a neutral badge; anything short of that is coloured. That contrast is
// the point — it is what lets someone scan a column of collapsed group headers
// and spot the one group with an exclusion without opening any of them.
const CountBadge = ({ inTest, total, suffix = '' }) => (
  <Badge size="sm" variant="light" color={inTest === total ? 'gray' : 'yellow'}>
    {`${inTest} / ${total}${suffix}`}
  </Badge>
);

// One feed row, identical whether it is listed inline or inside the
// cannot-be-tested disclosure.
const FeedCheckbox = ({ feed, checked, disabled, onChange }) => (
  <Checkbox
    size="sm"
    mb={6}
    checked={checked}
    disabled={disabled}
    onChange={onChange}
    label={
      <Group gap={6}>
        <Badge size="xs" color="gray" variant="light">
          {feedSurfaceLabel[feed.surface]}
        </Badge>
        <Text size="sm">{feed.name}</Text>
        <Text size="xs" c="dimmed">
          {feed.algorithm}
        </Text>
        {!feed.eligible && (
          <Text size="xs" c="orange">
            {ineligibleReasonLabel[feed.ineligibleReason] || 'not eligible'}
          </Text>
        )}
      </Group>
    }
  />
);

// The arms one placement may test: the DRAFT pool narrowed to the placement's
// scope, minus its own configured feed — that one always runs as an arm, so
// it is never something to pick. Ineligible feeds are left out even when they
// are still pooled: the API serves only eligible pooled feeds as arms and
// rejects an override that names any other (see `prunedOverrides`).
const overrideChoices = (poolFeeds, feedIds, primaryFeedId) =>
  (poolFeeds || []).filter((feed) => feed.eligible && feedIds.includes(feed.id) && feed.id !== primaryFeedId);

// One placement's arm selection. The two states are modelled explicitly rather
// than inferred from the selection, because "every pool feed" and "all of
// today's pool feeds, listed" are different things: only the first picks up a
// feed added to the pool later.
const FeedOverrideControl = ({ name, label, override, choices, poolReadable, disabled, onChange }) => {
  const custom = Array.isArray(override);
  // A feed the operator has just unticked from the pool stops being a choice,
  // so it drops out of what this shows — the same pruning the payload does.
  // But that pruning only runs when the pool is readable (see `prune` in
  // `placementsPayload`); `choices` is unconditionally [] on the unreadable
  // path, and filtering against it there would show "nothing to compare" while
  // the PUT still sends the override intact. So on that path render the stored
  // ids as-is instead of narrowing them to `choices`.
  const selected = custom
    ? poolReadable
      ? override.filter((id) => choices.some((feed) => feed.id === id))
      : override
    : [];
  const choiceIds = new Set(choices.map((feed) => feed.id));
  const extraIds = poolReadable ? [] : selected.filter((id) => !choiceIds.has(id));
  const data = [
    ...choices.map((feed) => ({ value: String(feed.id), label: feed.name })),
    // Ids the unreadable pool can't explain get a bare label rather than
    // vanishing from the MultiSelect's data (Mantine drops values not present
    // in `data`).
    ...extraIds.map((id) => ({ value: String(id), label: `Feed ${id}` })),
  ];
  // Ids are opaque elsewhere in the panel (`draft.feedIds.includes(feed.id)`);
  // looking the stringified MultiSelect value back up in `data` keeps that true
  // here too, instead of assuming `Number(value)` round-trips every id.
  const idByValue = new Map([
    ...choices.map((feed) => [String(feed.id), feed.id]),
    ...extraIds.map((id) => [String(id), id]),
  ]);
  return (
    <Box mb={6}>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <SegmentedControl
          name={name}
          aria-label={`${label} mode`}
          size="xs"
          value={custom ? 'custom' : 'all'}
          data={[
            { value: 'all', label: 'All pool feeds' },
            { value: 'custom', label: 'Custom' },
          ]}
          disabled={disabled}
          onChange={(value) => onChange(value === 'custom' ? selected : null)}
        />
        {custom && (
          <MultiSelect
            size="xs"
            flex={1}
            searchable
            aria-label={label}
            placeholder="Pick the feeds to test"
            data={data}
            value={selected.map(String)}
            disabled={disabled}
            onChange={(values) => onChange(values.map((value) => idByValue.get(value) ?? value))}
          />
        )}
      </Group>
      {custom && selected.length === 0 && (
        <Text size="xs" c="orange" mt={4}>
          Only the primary feed will be served — nothing to compare.
        </Text>
      )}
    </Box>
  );
};

const ArmsTable = ({ arms, currency }) => (
  <Table striped withTableBorder highlightOnHover>
    <Table.Thead>
      <Table.Tr>
        <Table.Th>Feed</Table.Th>
        <Table.Th ta="right">Served</Table.Th>
        <Table.Th ta="right">Clicks</Table.Th>
        <Table.Th ta="right">Conversions</Table.Th>
        <Table.Th ta="right">Revenue</Table.Th>
        <Table.Th w={180}>Win probability</Table.Th>
      </Table.Tr>
    </Table.Thead>
    <Table.Tbody>
      {arms.map((arm) => {
        // An arm the API has not ranked gets an explicit "gathering data"
        // instead of a 0% bar, which reads as a measured near-zero chance.
        const ranked = arm.beliefState !== 'gathering-data' && arm.winProbability != null;
        return (
          <Table.Tr key={arm.productFeedId}>
            <Table.Td>
              <Group gap="xs">
                <Text size="sm">{arm.feedName}</Text>
                {arm.isPrimary && (
                  <Badge size="xs" color="gray" variant="light">
                    Primary
                  </Badge>
                )}
                {arm.favored && (
                  <Badge size="xs" color="teal" variant="filled">
                    Leading
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {arm.algorithm}
              </Text>
            </Table.Td>
            <Table.Td ta="right">{formatCount(arm.decisions)}</Table.Td>
            <Table.Td ta="right">{formatCount(arm.clicks)}</Table.Td>
            <Table.Td ta="right">{formatCount(arm.conversions)}</Table.Td>
            <Table.Td ta="right">{formatCurrency(arm.revenue, currency)}</Table.Td>
            <Table.Td>
              {ranked ? (
                <Group gap="xs" wrap="nowrap">
                  <Progress value={arm.winProbability * 100} color={arm.favored ? 'teal' : 'gray'} w={110} size="sm" />
                  <Text size="xs" w={36} ta="right">
                    {formatRatio(arm.winProbability)}
                  </Text>
                </Group>
              ) : (
                <Text size="xs" c="dimmed">
                  Gathering data
                </Text>
              )}
            </Table.Td>
          </Table.Tr>
        );
      })}
    </Table.Tbody>
  </Table>
);

// A placement in the test with fewer than two arms has nothing to compare, but
// is shown saying so rather than silently dropped — it is the answer to "why is
// my block not in the results?". It lives behind its group's disclosure, since
// it carries no measurement to scan.
const UntestablePlacement = ({ name }) => (
  <Box mb="md">
    <Text size="sm" fw={500} mb={4}>
      {name}
    </Text>
    <Text size="sm" c="dimmed">
      Not enough feeds to test — a placement needs at least two arms.
    </Text>
  </Box>
);

const totalServed = (arms) => arms.reduce((sum, arm) => sum + (arm.decisions || 0), 0);

// The line that stands in for a whole arms table: enough to answer "has this
// placement learned anything?" without opening it.
const PlacementSummary = ({ name, arms }) => {
  const leading = arms.some((arm) => arm.favored);
  // Placement-level, deliberately not ArmsTable's per-arm predicate above:
  // that one asks "can I show a bar for this arm?", this one asks "has the API
  // ranked anything here at all?". A ranked-but-unfavoured placement is
  // mid-test, so it gets neither the badge nor "Gathering data".
  const ranked = arms.some((arm) => arm.beliefState === 'ranked');
  return (
    // Every text child truncates so the summary stays one line — Mantine's
    // `truncate` is white-space: nowrap plus an ellipsis.
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" truncate>
        {name}
      </Text>
      <Text size="xs" c="dimmed" truncate>
        {`${arms.length} arms · ${formatCount(totalServed(arms))} served`}
      </Text>
      {leading && (
        <Badge size="xs" color="teal" variant="filled">
          Leading
        </Badge>
      )}
      {!leading && !ranked && (
        <Text size="xs" c="dimmed" truncate>
          Gathering data
        </Text>
      )}
    </Group>
  );
};

// One results group (a page, Emails, or Search), collapsed. Its placements are
// one-line summaries with their arms tables behind a second expansion — unlike
// the Configuration card, which folds at group level only, because the atom
// there is a checkbox and here it is a six-column table.
const ResultsGroup = ({ value, label, placements, currency }) => {
  const [showUntestablePlacements, setShowUntestablePlacements] = useState(false);
  const testablePlacements = placements.filter((placement) => placement.arms.length >= 2);
  const untestablePlacements = placements.filter((placement) => placement.arms.length < 2);
  // Count and total are over every placement in the group, including the ones
  // with nothing to compare: the badge answers "how much is in here", and a
  // one-arm block is still serving.
  const served = totalServed(placements.flatMap((placement) => placement.arms));

  return (
    <Accordion.Item value={value}>
      <Accordion.Control>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" truncate>
            {label}
          </Text>
          <Badge size="sm" variant="light" color="gray">
            {`${placements.length} placement${placements.length === 1 ? '' : 's'} · ${formatCount(served)} served`}
          </Badge>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        {testablePlacements.length > 0 && (
          // No `order` here, unlike the outer accordion: a placement sits
          // inside its group's <h6>, so giving it a heading too would either
          // repeat the level or need an h7 that does not exist.
          <Accordion multiple>
            {testablePlacements.map((placement) => (
              <Accordion.Item key={placement.key} value={placement.key}>
                <Accordion.Control>
                  <PlacementSummary name={placement.name} arms={placement.arms} />
                </Accordion.Control>
                <Accordion.Panel>
                  <ArmsTable arms={placement.arms} currency={currency} />
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
        {untestablePlacements.length > 0 && (
          <>
            <Button
              size="xs"
              variant="subtle"
              px={0}
              onClick={() => setShowUntestablePlacements((shown) => !shown)}
              aria-expanded={showUntestablePlacements}
            >
              {`${showUntestablePlacements ? 'Hide' : 'Show'} ${untestablePlacements.length} placement${
                untestablePlacements.length === 1 ? '' : 's'
              } with nothing to compare yet`}
            </Button>
            <Collapse expanded={showUntestablePlacements}>
              {untestablePlacements.map((placement) => (
                <UntestablePlacement key={placement.key} name={placement.name} />
              ))}
            </Collapse>
          </>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

const FeedBanditPanel = ({ domain }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const base = `/api/admin/accounts/${accountId}/domains/${domainId}/feed-bandit`;

  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(emptyConfig);
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState(daysAgoISODate(29));
  const [endDate, setEndDate] = useState(todayISODate());
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  // Which write is in flight, if any: 'toggle' (the kill switch) or 'save' (the
  // config PUT), null when idle. One flag rather than one per operation,
  // because the lock it implies is the same for both — both re-sync every
  // recommender and email on the domain, so they must not overlap with each
  // other or with an edit to the draft. Every control in the Configuration card
  // that can change the draft reads `busy` (the disclosure toggle below does
  // not — it only shows and hides rows); only the Save spinner cares which
  // write it is.
  const [busy, setBusy] = useState(null);
  // Whether the "feeds that cannot be tested" disclosure is open. Purely
  // presentational — on a real domain two thirds of the feed list is
  // un-tickable, and leaving it inline buries the feeds the operator can act on.
  const [showUntestable, setShowUntestable] = useState(false);
  const [error, setError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  // Guard against out-of-order responses: a mutation reloads from an old render
  // while the mount load may still be in flight, so only the newest response of
  // each kind may write state.
  const stateRequestId = useRef(0);
  const statsRequestId = useRef(0);
  // The configKey of the last config we knew the server to have, or null when
  // we have no such knowledge and the next response should win outright.
  // A reseed compares the freshly-loaded config against this, not against
  // the draft: the pool is only auto-filled on a genuine first activation, so
  // on the second and later ON toggles the response just echoes back what
  // was already there, and a staged-but-unsaved edit should survive. Diffing
  // against the draft instead would mistake "the user edited it locally" for
  // "the server changed it" and reseed anyway.
  const lastConfigKeyRef = useRef(null);

  // A reload re-seeds the draft only when the server's canonical config
  // actually moved. Switching the bandit on auto-fills the pool server-side
  // on a genuine first activation, so that response has to win over anything
  // staged; a repeat ON, a Save, or an OFF that changed nothing behaves the
  // same as leaving the draft alone. Turning the bandit off (or a Retry)
  // auto-fills nothing, so those callers pass reseed: false outright.
  const loadState = useCallback(
    async (reseed = true) => {
      const id = ++stateRequestId.current;
      setError(null);
      try {
        const s = await fetch(base);
        if (id !== stateRequestId.current) return;
        const newConfigKey = configKey(configFromState(s));
        if (reseed && newConfigKey !== lastConfigKeyRef.current) setDraft(configFromState(s));
        lastConfigKeyRef.current = newConfigKey;
        setState(s);
      } catch (err) {
        if (id === stateRequestId.current) setError(err.message || 'Failed to load feed bandit');
      } finally {
        if (id === stateRequestId.current) setLoading(false);
      }
    },
    [fetch, base]
  );

  // The results window lives in the dependencies rather than in a ref: every
  // caller runs the newest closure, so there is no stale window to read.
  const loadStats = useCallback(async () => {
    const id = ++statsRequestId.current;
    setStatsError(null);
    setStatsLoading(true);
    const params = new URLSearchParams({
      from: dateToStartOfDay(startDate, 'local'),
      to: dateToEndOfDay(endDate, 'local'),
    });
    try {
      const st = await fetch(`${base}/stats?${params.toString()}`);
      if (id !== statsRequestId.current) return;
      setStats(st);
    } catch (err) {
      if (id === statsRequestId.current) setStatsError(err.message || 'Failed to load the results');
    } finally {
      if (id === statsRequestId.current) setStatsLoading(false);
    }
  }, [fetch, base, startDate, endDate]);

  // Clearing the ref here rather than seeding it at declaration is what ties
  // its lifetime to the domain it is a cache of: loadState's identity changes
  // with `base`, so a domain switch under a mounted panel drops the previous
  // domain's key and the new domain's first response seeds the draft outright.
  useEffect(() => {
    lastConfigKeyRef.current = null;
    loadState();
  }, [loadState]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // The kill switch is deliberately not staged behind Save. The ON transition
  // passes reseed: true, but loadState only actually overwrites the draft if
  // the server's config really moved (a genuine first-activation auto-fill) —
  // a repeat ON leaves a staged edit alone. Turning it OFF auto-fills
  // nothing, so it passes reseed: false outright.
  const toggle = async (enabled) => {
    setBusy('toggle');
    setError(null);
    try {
      await fetch(base, { method: 'PUT', body: JSON.stringify({ enabled }) });
      await Promise.all([loadState(enabled), loadStats()]);
    } catch (err) {
      setError(err.message || 'Failed to change the feed bandit');
    } finally {
      setBusy(null);
    }
  };

  // One transaction for pool membership and participation: the API re-syncs
  // every recommender and email on the domain per write, so the draft is sent
  // as full membership, once.
  const saveConfig = async () => {
    setBusy('save');
    setError(null);
    try {
      await fetch(`${base}/config`, {
        method: 'PUT',
        body: JSON.stringify(configBody(state, draft)),
      });
      // A save is authoritative: whatever the server reports back is now the
      // truth, even when it did not persist the edit verbatim (an unknown
      // recommender token dropped, a normalised pool). Clearing the ref makes
      // the reload below re-seed unconditionally, so a draft can never be left
      // permanently dirty against a config the server declined to store.
      lastConfigKeyRef.current = null;
      await Promise.all([loadState(), loadStats()]);
    } catch (err) {
      setError(err.message || 'Failed to save the configuration');
    } finally {
      setBusy(null);
    }
  };

  const toggleDraftMember = (key, value, on) =>
    setDraft((d) => ({
      ...d,
      [key]: on ? [...new Set([...d[key], value])] : d[key].filter((v) => v !== value),
    }));

  const setBlockFeeds = (token, value) =>
    setDraft((d) => ({ ...d, recommenderFeeds: { ...d.recommenderFeeds, [token]: value } }));

  const setEmailBlockFeeds = (emailId, blockIndex, value) =>
    setDraft((d) => ({
      ...d,
      emailBlockFeeds: { ...d.emailBlockFeeds, [emailId]: { ...d.emailBlockFeeds[emailId], [blockIndex]: value } },
    }));

  const currency = domain?.currency || '';
  // Memoised so the two JSON.stringify passes inside configKey only re-run
  // when the saved state actually changes, not on every render (e.g. the date
  // picker moving stats around).
  const savedConfig = useMemo(() => configFromState(state), [state]);
  const dirty = configKey(draft) !== configKey(savedConfig);

  if (loading) {
    return (
      <Card withBorder>
        <Group>
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading feed bandit…
          </Text>
        </Group>
      </Card>
    );
  }

  // A failed load leaves state null — show the error rather than the empty-state
  // copy ("No blocks…"), which would misread as a healthy empty domain.
  if (error && !state) {
    return (
      <Card withBorder>
        <Alert color="red" variant="light" icon={<IconInfoCircle size={16} />}>
          {error}
        </Alert>
        <Button
          mt="sm"
          size="xs"
          variant="light"
          onClick={() => {
            setLoading(true);
            loadState();
            loadStats();
          }}
        >
          Retry
        </Button>
      </Card>
    );
  }

  const pages = (state?.placements?.web || []).filter((page) => (page.blocks || []).length);
  const emails = state?.placements?.email || [];
  const searchPlacement = state?.placements?.search;
  // One column, with the surface as a chip per row, rather than two stacked
  // lists — a feed belongs to exactly one of the two.
  const feeds = [
    ...(state?.pool?.web || []).map((feed) => ({ ...feed, surface: 'web' })),
    ...(state?.pool?.channel || []).map((feed) => ({ ...feed, surface: 'channel' })),
  ];

  const feedRow = (feed) => (
    <FeedCheckbox
      key={`${feed.surface}:${feed.id}`}
      feed={feed}
      checked={draft.feedIds.includes(feed.id)}
      disabled={!!busy || !feed.eligible}
      onChange={(e) => toggleDraftMember('feedIds', feed.id, e.currentTarget.checked)}
    />
  );

  // A feed the operator cannot tick AND that is not an arm of the running test
  // is noise — it goes behind the disclosure. The pool half of the predicate is
  // load-bearing: the API can legitimately report { eligible: false, inPool:
  // true } (a feed pooled before the allowlist narrowed), and that feed IS
  // being served, so hiding it would misrepresent the pool as smaller than it
  // is with no way for the operator to discover why. Membership is read from
  // the draft so a feed does not vanish mid-edit.
  const untestable = (feed) => !feed.eligible && !draft.feedIds.includes(feed.id);
  const testableFeeds = feeds.filter((feed) => !untestable(feed));
  const untestableFeeds = feeds.filter(untestable);
  const webOverrides = webOverridesDescribed(state);
  const emailOverrides = emailOverridesDescribed(state);
  // Same guard as `prune` in `placementsPayload`: `draft.feedIds` is only a
  // complete pool when both halves were read, so `overrideChoices` below is
  // only trustworthy under the same condition.
  const poolReadable = Array.isArray(state?.pool?.web) && Array.isArray(state?.pool?.channel);

  // One group per page, plus Emails and Search — exactly the grouping the flat
  // list had. Counts come off the draft, not the saved state, so a header moves
  // the instant a checkbox inside it is ticked.
  const blockGroups = [
    ...pages.map((page, index) => ({
      value: `page:${page.pageToken || page.pageId || index}`,
      label: page.pageName,
      inTest: page.blocks.filter((block) => draft.recommenderTokens.includes(block.token)).length,
      total: page.blocks.length,
      content: page.blocks.map((block) => (
        <Box key={block.token} mb={8}>
          <Checkbox
            size="sm"
            mb={4}
            checked={draft.recommenderTokens.includes(block.token)}
            disabled={!!busy}
            onChange={(e) => toggleDraftMember('recommenderTokens', block.token, e.currentTarget.checked)}
            label={
              <Group gap={6} wrap="nowrap">
                <Text size="sm">{block.name}</Text>
                {block.primaryFeed?.name && (
                  <Text size="xs" c="dimmed">
                    now serving {block.primaryFeed.name}
                  </Text>
                )}
              </Group>
            }
          />
          {webOverrides && (
            <Box ml={30}>
              <FeedOverrideControl
                name={`blockFeeds:${block.token}`}
                label={`Feeds for ${block.name}`}
                override={draft.recommenderFeeds[block.token]}
                choices={overrideChoices(state?.pool?.web, draft.feedIds, block.primaryFeed?.id)}
                poolReadable={poolReadable}
                disabled={!!busy || !draft.recommenderTokens.includes(block.token)}
                onChange={(value) => setBlockFeeds(block.token, value)}
              />
            </Box>
          )}
        </Box>
      )),
    })),
    ...(emails.length
      ? [
          {
            value: 'emails',
            label: 'Emails',
            inTest: emails.filter((email) => draft.emailIds.includes(email.emailId)).length,
            total: emails.length,
            content: emails.map((email) => (
              <Box key={email.emailId} mb={8}>
                <Checkbox
                  size="sm"
                  mb={4}
                  label={email.name}
                  checked={draft.emailIds.includes(email.emailId)}
                  disabled={!!busy}
                  onChange={(e) => toggleDraftMember('emailIds', email.emailId, e.currentTarget.checked)}
                />
                {emailOverrides &&
                  email.blocks.map((block) => (
                    <Box key={block.blockIndex} ml={30}>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm">{`Block ${block.blockIndex + 1}`}</Text>
                        {block.primaryFeed?.name && (
                          <Text size="xs" c="dimmed">
                            now serving {block.primaryFeed.name}
                          </Text>
                        )}
                      </Group>
                      <FeedOverrideControl
                        name={`emailBlockFeeds:${email.emailId}:${block.blockIndex}`}
                        label={`Feeds for ${email.name} block ${block.blockIndex + 1}`}
                        override={draft.emailBlockFeeds[email.emailId]?.[block.blockIndex]}
                        choices={overrideChoices(state?.pool?.channel, draft.feedIds, block.primaryFeed?.id)}
                        poolReadable={poolReadable}
                        disabled={!!busy || !draft.emailIds.includes(email.emailId)}
                        onChange={(value) => setEmailBlockFeeds(email.emailId, block.blockIndex, value)}
                      />
                    </Box>
                  ))}
              </Box>
            )),
          },
        ]
      : []),
    ...(searchPlacement
      ? [
          {
            value: 'search',
            label: 'Search',
            inTest: draft.search ? 1 : 0,
            total: 1,
            content: (
              <Checkbox
                size="sm"
                label="Search results"
                checked={draft.search}
                disabled={!!busy}
                onChange={(e) => setDraft((d) => ({ ...d, search: e.currentTarget.checked }))}
              />
            ),
          },
        ]
      : []),
  ];
  const blocksInTest = blockGroups.reduce((sum, group) => sum + group.inTest, 0);
  const blocksTotal = blockGroups.reduce((sum, group) => sum + group.total, 0);

  const groups = stats?.groups || [];
  const resultPages = (groups.find((g) => g.surface === 'web')?.pages || [])
    .map((page) => ({ ...page, blocks: (page.blocks || []).filter(participates) }))
    .filter((page) => page.blocks.length);
  const resultEmails = (groups.find((g) => g.surface === 'email')?.emails || []).filter(participates);
  const searchGroup = groups.find((g) => g.surface === 'search')?.search;
  const resultSearch = searchGroup && participates(searchGroup) ? searchGroup : null;
  const hasResults = resultPages.length || resultEmails.length || resultSearch;

  // The same grouping the stacked sections had — one group per page, plus
  // Emails and Search — reshaped into the Configuration card's group/member
  // form so it can be collapsed the same way.
  //
  // Every `value`/`key` below is a non-empty prefix plus an id-or-index
  // fallback, without exception. These are Accordion.Item values, not just
  // React keys: Mantine builds its control and region ids from them with
  // getSafeId, which throws out of render for anything that is not a non-blank
  // string. A stats payload reporting a block with a missing, empty or numeric
  // `token` would take the page down — there is no error boundary above this.
  const resultGroups = [
    ...resultPages.map((page, index) => ({
      value: `page:${page.pageToken || page.pageId || index}`,
      label: page.pageName,
      placements: page.blocks.map((block, blockIndex) => ({
        key: `block:${block.token || blockIndex}`,
        name: block.name,
        arms: block.arms || [],
      })),
    })),
    ...(resultEmails.length
      ? [
          {
            value: 'emails',
            label: 'Emails',
            placements: resultEmails.map((email, emailIndex) => ({
              key: `email:${email.emailId || emailIndex}`,
              name: email.name,
              arms: email.arms || [],
            })),
          },
        ]
      : []),
    ...(resultSearch
      ? [
          {
            value: 'search',
            label: 'Search',
            placements: [{ key: 'search', name: 'Search results', arms: resultSearch.arms || [] }],
          },
        ]
      : []),
  ];

  return (
    <Stack gap="md">
      <Card withBorder>
        <Grid align="center" gutter="md">
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <Text size="sm" c="dimmed">
              Per-shopper feed learning. When on, the blocks, emails and search you pick below test this domain&apos;s
              pooled feeds against each other and serve each shopper the feed that converts best for them. Turning it
              off is an immediate kill switch — every placement falls back to its configured feed.
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Group justify="flex-end">
              <Switch
                name="banditEnabled"
                size="lg"
                labelPosition="left"
                label={state?.enabled ? 'On' : 'Off'}
                checked={!!state?.enabled}
                disabled={!!busy}
                onChange={(event) => toggle(event.currentTarget.checked)}
              />
            </Group>
          </Grid.Col>
        </Grid>
      </Card>

      {error && (
        <Alert color="red" variant="light" icon={<IconInfoCircle size={16} />}>
          {error}
        </Alert>
      )}

      <Card withBorder>
        <Card.Section inheritPadding py="sm" withBorder>
          <Group justify="space-between">
            <Text fw={600}>Configuration</Text>
            {dirty && (
              <Badge color="yellow" variant="light">
                Unsaved changes
              </Badge>
            )}
          </Group>
        </Card.Section>

        <Card.Section inheritPadding py="sm">
          <Group justify="space-between" wrap="nowrap">
            <Title order={6}>Blocks in the test</Title>
            {blockGroups.length > 0 && <CountBadge inTest={blocksInTest} total={blocksTotal} suffix=" in test" />}
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Everything takes part by default. Uncheck a block, an email or search to leave it serving its own configured
            feed instead of being experimented on. A block that takes part tests every pooled feed unless you narrow it
            to a custom selection — its own configured feed always runs as an arm either way.
          </Text>
          {blockGroups.length === 0 ? (
            <Text size="sm" c="dimmed">
              This domain has no blocks, emails or search to test.
            </Text>
          ) : (
            // Collapsed by default, always — the counts on the controls are the
            // signal, so auto-expanding a group with an exclusion would make the
            // screen jump around for no extra information.
            <Accordion multiple variant="contained">
              {blockGroups.map((group) => (
                <Accordion.Item key={group.value} value={group.value}>
                  <Accordion.Control>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm">{group.label}</Text>
                      <CountBadge inTest={group.inTest} total={group.total} />
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>{group.content}</Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Card.Section>

        <Card.Section inheritPadding py="sm">
          <Title order={6}>Feeds being tested</Title>
          <Text size="xs" c="dimmed" mb="sm">
            These are this domain&apos;s own product feeds — the arms a placement picks between, not the blocks
            themselves. The pool is auto-filled the first time you switch the bandit on, using every eligible feed;
            after that it&apos;s yours to curate, and a feed created later is{' '}
            <Text span fw={700}>
              not
            </Text>{' '}
            added automatically. A feed that cannot be an arm is listed with the reason and cannot be ticked — usually
            behind the disclosure below, unless it is already in the pool. A placement&apos;s own configured feed always
            runs as an arm even when it is not in the pool — it is the incumbent the test has to beat, and it appears in
            Results marked Primary. This pool is what every block starts from; narrowing a single block to a subset of
            it is done on that block, under Blocks in the test.
          </Text>
          {feeds.length === 0 ? (
            <Text size="sm" c="dimmed">
              This domain has no product feeds.
            </Text>
          ) : (
            <>
              {testableFeeds.map(feedRow)}
              {untestableFeeds.length > 0 && (
                <>
                  <Button
                    size="xs"
                    variant="subtle"
                    px={0}
                    onClick={() => setShowUntestable((shown) => !shown)}
                    aria-expanded={showUntestable}
                  >
                    {`${showUntestable ? 'Hide' : 'Show'} ${untestableFeeds.length} feed${
                      untestableFeeds.length === 1 ? '' : 's'
                    } that cannot be tested`}
                  </Button>
                  {/* Mantine gives a collapsed Collapse aria-hidden, inert and
                      display: none, so a feed behind the disclosure is out of
                      the accessibility tree until it is opened — the same
                      mounting the Accordion above uses, left at the default. */}
                  <Collapse expanded={showUntestable}>{untestableFeeds.map(feedRow)}</Collapse>
                </>
              )}
            </>
          )}
        </Card.Section>

        <Card.Section inheritPadding py="sm" withBorder>
          <Group>
            <Button size="xs" onClick={saveConfig} loading={busy === 'save'} disabled={!dirty || !!busy}>
              Save
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setDraft(savedConfig)} disabled={!dirty || !!busy}>
              Discard changes
            </Button>
            {dirty && (
              <Text size="xs" c="dimmed">
                Nothing is applied until you save.
              </Text>
            )}
          </Group>
        </Card.Section>
      </Card>

      <Card withBorder>
        <Card.Section inheritPadding py="sm" withBorder>
          <Group justify="space-between">
            <Text fw={600}>Results</Text>
            <MetricsSelector
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
          </Group>
          {/* The bandit being off doesn't mean nothing happened in this window
              — it means nothing is being served live right now. Say so,
              rather than let a history table with Leading badges read as
              current. */}
          {!state?.enabled && (
            <Text size="xs" c="dimmed" mt={4}>
              The feed bandit is off — these are historical results, not what is being served right now.
            </Text>
          )}
        </Card.Section>

        <Card.Section inheritPadding py="sm">
          {/* The empty-state copy is deliberately not shown next to an error —
              it would read as "nothing is running" rather than "not loaded". */}
          {statsError ? (
            <Alert color="red" variant="light" icon={<IconInfoCircle size={16} />}>
              {statsError}
            </Alert>
          ) : statsLoading ? (
            <Group>
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading results…
              </Text>
            </Group>
          ) : !hasResults ? (
            <Text size="sm" c="dimmed">
              No experiments running yet. Once the placements in the test have a pool of feeds, each will appear here
              with per-feed results as traffic comes in.
            </Text>
          ) : (
            // Collapsed by default, always — the summary on each control is the
            // signal, and a group opened for you is a group you did not choose
            // to read.
            //
            // `order` wraps each group's control in an <h6>, which is how the
            // card keeps heading navigation now that the three Title order={6}
            // section headings are gone — same level those sat at, and the same
            // level the Configuration card's own Titles still sit at. Mantine
            // documents it as having no visual effect, and the control stays the
            // same button with the same accessible name inside the heading.
            <Accordion multiple variant="contained" order={6}>
              {resultGroups.map((group) => (
                <ResultsGroup key={group.value} {...group} currency={currency} />
              ))}
            </Accordion>
          )}
        </Card.Section>
      </Card>
    </Stack>
  );
};

export default FeedBanditPanel;
